import PassengerType from "@CheckOrder/types/Passenger.type";
import IntermodalPassengerType from "@CheckOrder/types/IntermodalPassenger.type";
import {useAppSelector} from "@Hooks/selector/useAppSelector";
import usePrepareAdditionalInfoForPassengerSubmit
    from "@CheckOrder/features/TicketAdditionalInfo/functions/usePrepareAdditionalInfoForPassengerSubmit";
import {useLazyUpdatePassengerQuery} from "@CheckOrder/api/checkOrderApi";
import {UpdatePassengerRequestType} from "@CheckOrder/api/types/UpdatePassengerQuery.type";

export default function useSubmitPassengers(handleError, cancelToken) {
    const {
        isIntermodal,
    } = useAppSelector((state) => state?.checkOrder?.ticket)
    // TODO add cancel token (or simulate it)
    const [updatePassengerQuery] = useLazyUpdatePassengerQuery()
    const {prepareAdditionalInfo} = usePrepareAdditionalInfoForPassengerSubmit()
    const updatePassenger = async (params: UpdatePassengerRequestType) => {
        const response = await updatePassengerQuery(params).unwrap()
        return response.data?.passenger?.passengerId
    }

    const areIntermodalPassengers = (_passengers: PassengerType[] | IntermodalPassengerType[], isIntermodal: boolean): _passengers is IntermodalPassengerType[] => (isIntermodal)

    const submitRegularPassengers = (passengers: PassengerType[]): Promise<Awaited<number>[]> => (
        Promise.all(passengers.map(async (passenger) => (
            updatePassenger({
                ...passenger,
                // We only care about the ISO, ignore the name and dial code
                nationality: passenger?.nationality?.isoCode,
                additionalInfo: prepareAdditionalInfo(passenger.additionalInfo),
                returnAdditionalInfo: prepareAdditionalInfo(passenger.returnAdditionalInfo),
            })
        )))
    )

    const submitIntermodalPassengers = async (passengers: IntermodalPassengerType[]): Promise<Awaited<number>[]> => (
        Promise.all(passengers.flatMap((passenger) => (
            passenger.basePassengers.map(async (basePassenger) => (
                updatePassenger({
                    ...basePassenger,
                    dateOfBirth: passenger.dateOfBirth,
                    gender: passenger.gender,
                    nationality: passenger?.nationality?.isoCode,
                    passengerFirstName: passenger.passengerFirstName,
                    passengerLastName: passenger.passengerLastName,
                    travelDocument: passenger.travelDocument,
                    passengerDocumentCode: passenger.passengerDocumentCode,
                })
            ))
        )))
    )

    const submitPassengers = async (passengers: PassengerType[] | IntermodalPassengerType[]): Promise<Awaited<number>[]> => (
        areIntermodalPassengers(passengers, isIntermodal) ? submitIntermodalPassengers(passengers) : submitRegularPassengers(passengers)
    )

    return {
        submitPassengers,
    }
}